/*
	Cart rewards bar engine.

	A theme block cannot read its children, so the bar cannot be drawn server-side as one
	piece. Each `cart-progress-tier` renders its own segment carrying only what a tier knows
	alone, and everything that depends on the whole set is worked out here: segment widths,
	how full each one is, which tier is next, the message, and keeping free gift line items
	in sync.

	The cart drawer re-renders by swapping `.drawer__inner` for freshly server-rendered markup,
	so the bar is a brand new element after every cart change with no memory of where its fill
	used to be. The measure pass always produces the correct *final* state; paint's only job is
	to replay the journey to it.

	Continuity trick: the engine holds no element state, only a module-level map of block id to
	the last painted fills. When it meets an uninitialised bar it restores those previous fills
	with transitions suppressed, forces a reflow, then releases them and writes the measured
	target. The MutationObserver callback runs as a microtask, before the browser paints the
	inserted node, so the old value is in place in time and nothing flashes.

	Sweep scheduling: a single cart update can cross more than one tier (a bundle add, a big
	qty jump), meaning more than one segment's fill changes in the same paint. Rather than
	animate every changed segment over the same fixed duration (which makes one finish while
	an unrelated one is still getting started, reading as two separate motions), paint()
	works out the share of the total travel distance each segment is responsible for, so the
	fill reads as one continuous sweep moving through however many segments it needs to cross.

	Easing across that sweep: the whole journey follows one ease-in-out curve, and each
	segment runs for the full duration with a timing function that is that curve restricted
	to its own stretch of the distance, holding at 0 before the leading edge arrives and at 1
	after it leaves. Because every segment is sampling the same curve on the same clock, the
	edge accelerates and settles once over the whole bar instead of once per segment, and the
	handovers land exactly on top of each other with no seam to schedule around.

	Markup contract:
		[data-cart-rewards="<block id>"]      bar root (snippets/cart-progress-bar.liquid)
		[data-cart-rewards-config]            JSON: mode, style, raw cart figures, duration
		[data-pgb-segment]                    one per tier (blocks/cart-progress-tier.liquid)
			[data-pgb-threshold]                threshold in cents
			[data-pgb-threshold-count]          threshold as an item count
			[data-pgb-gift-*]                   this tier's gift and what it is worth in the cart
			[data-pgb-message-locked]           message template before this tier unlocks
			[data-pgb-target]                   written here: fill percentage it settles on
			[data-pgb-state]                    written here: locked | unlocked
		[data-cart-rewards-message]           progress copy, filled here
*/
(function () {
	"use strict";

	var ROOT_SELECTOR = "[data-cart-rewards]";
	var SEGMENT_SELECTOR = "[data-pgb-segment]";
	var CELEBRATE_MS = 750;

	// blockId -> { fills: number[], states: string[], message: string }
	var memory = Object.create(null);
	var pendingTimers = [];
	var renderScheduled = false;
	var domObserver = null;

	var latestMeasure = null;
	var giftBusy = false;
	var giftSignature = null;
	var giftBlocklist = Object.create(null);

	// Stops used to describe a segment's slice of the sweep to CSS. Sampling is even in time
	// while the curve is not, so the fastest part of the sweep gets the fewest stops per unit
	// of distance; this is set high enough that even a narrow slice landing there stays smooth.
	var EASE_STOPS = 48;
	var linearEasingSupport = null;

	// Horizontal span of one stripe repeat: 16px of pattern measured along a -45deg axis.
	// Must match background-size in the stylesheet.
	var STRIPE_PERIOD = 22.627417;

	function prefersReducedMotion() {
		return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
	}

	// A multi-stop timing function is what lets several segments share one curve. Without it
	// the sweep falls back to constant speed, which is seamless but flat.
	function supportsLinearEasing() {
		if (linearEasingSupport === null) {
			linearEasingSupport = !!(
				window.CSS &&
				window.CSS.supports &&
				window.CSS.supports("transition-timing-function", "linear(0, 1)")
			);
		}
		return linearEasingSupport;
	}

	// easeInOutCubic, the same curve as cubic-bezier(0.65, 0, 0.35, 1) in the stylesheet.
	function easeInOut(t) {
		return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
	}

	// When during the sweep the leading edge reaches a given fraction of the total distance.
	function easeInOutInverse(progress) {
		if (progress <= 0) return 0;
		if (progress >= 1) return 1;
		if (progress < 0.5) return Math.pow(progress / 4, 1 / 3);
		return 1 - Math.pow(2 - 2 * progress, 1 / 3) / 2;
	}

	// The stretch of the sweep between `from` and `to` (both fractions of the total distance)
	// expressed as a CSS timing function for a transition running the full duration.
	function easeSliceTimingFunction(from, to) {
		var span = to - from;
		if (span <= 0) return null;

		var stops = [];
		for (var i = 0; i <= EASE_STOPS; i++) {
			var covered = (easeInOut(i / EASE_STOPS) - from) / span;
			if (covered < 0) covered = 0;
			if (covered > 1) covered = 1;
			stops.push(covered.toFixed(4));
		}
		return "linear(" + stops.join(",") + ")";
	}

	function clearPendingTimers() {
		for (var i = 0; i < pendingTimers.length; i++) clearTimeout(pendingTimers[i]);
		pendingTimers.length = 0;
	}

	function later(fn, delay) {
		pendingTimers.push(setTimeout(fn, delay));
	}

	function intOf(node, name) {
		var value = parseInt(node.getAttribute(name), 10);
		return isNaN(value) ? 0 : value;
	}

	function readConfig(root) {
		var node = root.querySelector("[data-cart-rewards-config]");
		if (!node) return null;
		try {
			return JSON.parse(node.textContent);
		} catch (e) {
			return null;
		}
	}

	function textOf(node) {
		return node ? node.textContent.replace(/\s+/g, " ").trim() : "";
	}

	function escapeHtml(value) {
		return String(value == null ? "" : value)
			.split("&")
			.join("&amp;")
			.split("<")
			.join("&lt;")
			.split(">")
			.join("&gt;")
			.split('"')
			.join("&quot;");
	}

	function fillTokens(template, tokens) {
		var out = String(template == null ? "" : template);
		for (var key in tokens) {
			if (Object.prototype.hasOwnProperty.call(tokens, key)) out = out.split(key).join(tokens[key]);
		}
		return out;
	}

	// The shop's format is already published globally by layout/theme.liquid, so the bar does
	// not carry its own copy.
	function formatMoney(cents) {
		var format = window.Shopify && window.Shopify.money_format;
		if (typeof window.Elixir_FormatMoney === "function") return window.Elixir_FormatMoney(cents, format);
		if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
			return window.Shopify.formatMoney(cents, format);
		}
		var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
		try {
			return new Intl.NumberFormat(undefined, { style: "currency", currency: currency }).format(cents / 100);
		} catch (e) {
			return (cents / 100).toFixed(2);
		}
	}

	// ----------------------------------------------------------------------------------
	// Measure: everything that needs to see every tier at once
	// ----------------------------------------------------------------------------------

	function readTiers(segments, mode, useOriginal) {
		var tiers = [];
		for (var i = 0; i < segments.length; i++) {
			var el = segments[i];
			tiers.push({
				el: el,
				threshold: mode === "quantity" ? intOf(el, "data-pgb-threshold-count") : intOf(el, "data-pgb-threshold"),
				giftId: intOf(el, "data-pgb-gift-variant"),
				giftAvailable: el.getAttribute("data-pgb-gift-available") === "true",
				giftValue: useOriginal ? intOf(el, "data-pgb-gift-value-original") : intOf(el, "data-pgb-gift-value"),
				giftQty: intOf(el, "data-pgb-gift-qty"),
				reward: el.getAttribute("data-pgb-reward") || "your reward",
				messageLocked: el.getAttribute("data-pgb-message-locked") || "",
			});
		}
		return tiers;
	}

	function warnIfUnordered(tiers) {
		if (!(window.Shopify && window.Shopify.designMode)) return;
		for (var i = 1; i < tiers.length; i++) {
			if (tiers[i].threshold < tiers[i - 1].threshold) {
				// eslint-disable-next-line no-console
				console.warn(
					"[Rewards Bar] Reward tiers are drawn in the order you arrange them, but their thresholds " +
						"go from high to low. Drag them into ascending order so the bar fills correctly."
				);
				return;
			}
		}
	}

	function measure(root) {
		var segments = root.querySelectorAll(SEGMENT_SELECTOR);
		if (!segments.length) return null;

		var config = readConfig(root) || {};
		var mode = config.mode === "quantity" ? "quantity" : "total";
		var useOriginal = !!config.useOriginal;
		var equalBlocks = config.equalBlocks !== false;

		var tiers = readTiers(segments, mode, useOriginal);
		warnIfUnordered(tiers);

		// Gifts must not count towards the thresholds that grant them, or a priced gift could
		// push the cart over the next tier and cascade one gift into the next.
		var giftValue = 0;
		var giftQty = 0;
		for (var g = 0; g < tiers.length; g++) {
			giftValue += tiers[g].giftValue;
			giftQty += tiers[g].giftQty;
		}
		var value =
			mode === "quantity"
				? Math.max(0, (config.rawCount || 0) - giftQty)
				: Math.max(0, ((useOriginal ? config.rawOriginalTotal : config.rawTotal) || 0) - giftValue);

		// Segments are laid out in the order the merchant dragged them into, each covering the
		// span from the tier before it to its own threshold.
		var floor = 0;
		var unlockedCount = 0;
		var activeIndex = -1;
		for (var i = 0; i < tiers.length; i++) {
			var tier = tiers[i];
			var span = Math.max(1, tier.threshold - floor);
			var filled = Math.max(0, value - floor);

			tier.fill = Math.max(0, Math.min(100, (filled * 100) / span));
			tier.grow = equalBlocks ? 1 : span;
			tier.isUnlocked = value >= tier.threshold;

			if (tier.isUnlocked) unlockedCount++;
			else if (activeIndex === -1) activeIndex = i;

			floor = tier.threshold;
		}

		var allUnlocked = unlockedCount === tiers.length;
		var active = allUnlocked ? tiers[tiers.length - 1] : tiers[activeIndex];
		var remaining = Math.max(0, active.threshold - value);

		applyGeometry(tiers, !!config.shine, !!config.striped, config.stripedAnimated !== false);
		writeMessage(root, config, active, allUnlocked, remaining, mode);

		return { tiers: tiers, value: value, mode: mode, useOriginal: useOriginal };
	}

	function applyGeometry(tiers, shine, striped, stripedAnimated) {
		// Each fill tiles the stripe pattern from its own left edge, which would restart the
		// pattern at every segment. Offsetting each one by its distance along the bar (modulo
		// a tile) puts them all on a single grid, so the stripes carry across the milestones
		// instead of stepping out of phase there. Measured once per pass, and only when the
		// pattern is actually on, to keep the layout read off the common path.
		var stripeOrigin = striped && tiers.length ? tiers[0].el.getBoundingClientRect().left : 0;

		for (var i = 0; i < tiers.length; i++) {
			var tier = tiers[i];
			tier.el.style.setProperty("--pgb-grow", String(tier.grow));
			tier.el.style.setProperty("--pgb-seg-fill", tier.fill + "%");
			tier.el.setAttribute("data-pgb-index", i);
			tier.el.setAttribute("data-pgb-target", tier.fill);
			tier.el.setAttribute("data-pgb-state", tier.isUnlocked ? "unlocked" : "locked");

			var fill = tier.el.querySelector(".cart-rewards__segment-fill");
			if (fill) {
				if (shine) fill.classList.add("elxr-shine");
				if (striped) {
					fill.classList.add("striped");
					fill.classList.toggle("striped--animated", stripedAnimated);
					var offset = tier.el.getBoundingClientRect().left - stripeOrigin;
					fill.style.setProperty("--pgb-stripe-shift", -(offset % STRIPE_PERIOD) + "px");
				}
			}
		}
	}

	function amountFor(remaining, mode) {
		return mode === "quantity" ? String(remaining) : formatMoney(remaining);
	}

	function writeMessage(root, config, active, allUnlocked, remaining, mode) {
		var textNode = root.querySelector("[data-cart-rewards-message-text]");
		if (!textNode) return;

		var amount = amountFor(remaining, mode);
		// Wrapped rather than styling the whole message so merchants can weight the amount alone.
		var amountHtml = '<span class="cart-rewards__amount">' + amount + "</span>";

		var template;
		if (allUnlocked) {
			template = config.completedMessage || "You've unlocked every reward!";
		} else {
			template = active.messageLocked || "You're {amount} away from {reward}!";
		}

		textNode.innerHTML = fillTokens(escapeHtml(template), {
			"{amount}": amountHtml,
			"{quantity}": amountHtml,
			"{reward}": escapeHtml(active.reward),
		});

		var iconNode = root.querySelector("[data-cart-rewards-message-icon]");
		if (iconNode) {
			var source = active.el.querySelector(".cart-rewards__node-icon--reward");
			iconNode.innerHTML = source ? source.innerHTML : "";
		}
	}

	// ----------------------------------------------------------------------------------
	// Paint: replay the journey to the measured state
	// ----------------------------------------------------------------------------------

	function snapshot(root, segments) {
		var fills = [];
		var states = [];
		for (var i = 0; i < segments.length; i++) {
			fills.push(parseFloat(segments[i].getAttribute("data-pgb-target")) || 0);
			states.push(segments[i].getAttribute("data-pgb-state") || "locked");
		}
		return {
			fills: fills,
			states: states,
			message: textOf(root.querySelector("[data-cart-rewards-message]")),
		};
	}

	function celebrate(segment) {
		segment.setAttribute("data-pgb-celebrate", "");
		later(function () {
			segment.removeAttribute("data-pgb-celebrate");
		}, CELEBRATE_MS);
	}

	function markChanged(node) {
		if (!node) return;
		node.setAttribute("data-pgb-changed", "");
	}

	function fillOf(segment) {
		return segment.querySelector(".cart-rewards__segment-fill");
	}

	function resetSegmentTiming(segment) {
		var fill = fillOf(segment);
		if (!fill) return;
		fill.style.transitionDuration = "";
		fill.style.transitionDelay = "";
		fill.style.transitionTimingFunction = "";
	}

	// A single cart update can cross more than one tier at once (a big qty jump, a bundle
	// add). Left alone, every changed segment would run the same fixed duration regardless
	// of how far it travels, so a segment finishing a short remaining stretch and the next
	// segment starting from zero would move at visibly different speeds and read as two
	// separate motions meeting at a seam. This carves the journey up instead: each segment
	// is handed the stretch of the total travel distance it is responsible for, as a pair of
	// fractions the easing above is then sliced along.
	function scheduleSweep(segments, previous, next) {
		var items = [];
		var sumFrom = 0;
		var sumTo = 0;
		for (var i = 0; i < segments.length; i++) {
			var from = previous.fills.length > i ? previous.fills[i] : next.fills[i];
			var to = next.fills[i];
			sumFrom += from;
			sumTo += to;
			if (from === to) continue;
			var widthPx = segments[i].getBoundingClientRect().width || 1;
			var weight = (Math.abs(to - from) / 100) * widthPx;
			items.push({ index: i, weight: weight > 0 ? weight : 0.01 });
		}
		if (!items.length) return items;

		// A retreat (items removed, value dropped) unwinds from the far end first, mirroring
		// the order the bar was originally filled in.
		if (sumTo < sumFrom) items.reverse();

		var totalWeight = 0;
		for (var w = 0; w < items.length; w++) totalWeight += items[w].weight;

		var elapsed = 0;
		for (var k = 0; k < items.length; k++) {
			var it = items[k];
			it.share = totalWeight > 0 ? it.weight / totalWeight : 1 / items.length;
			it.delayFraction = elapsed;
			elapsed += it.share;
		}
		return items;
	}

	function paint(root) {
		var segments = root.querySelectorAll(SEGMENT_SELECTOR);
		if (!segments.length) return;

		var id = root.getAttribute("data-cart-rewards");
		var config = readConfig(root) || {};
		var duration = typeof config.duration === "number" ? config.duration : 700;
		var previous = memory[id];
		var next = snapshot(root, segments);

		memory[id] = next;

		// Nothing worth animating when the merchant or the visitor has asked for stillness.
		if (duration <= 0 || prefersReducedMotion()) return;

		// A first paint has no remembered fills, but the tiers are server-rendered empty and
		// the stylesheet transitions width, so bailing out here did not mean "no animation":
		// it meant every segment ran 0 to target on its own clock, all at once. Standing in
		// an empty bar routes the intro through the same sweep as every later update, so it
		// reads as one edge travelling the bar rather than each segment filling in parallel.
		var isIntro = !previous;
		if (isIntro) {
			previous = { fills: [], states: [], message: next.message };
			for (var p = 0; p < segments.length; p++) {
				previous.fills.push(0);
				previous.states.push("locked");
			}
		}

		clearPendingTimers();

		var messageNode = root.querySelector("[data-cart-rewards-message]");
		if (previous.message !== next.message) markChanged(messageNode);

		root.setAttribute("data-pgb-animating", "off");
		eachSegment(segments, function (segment, index) {
			var from = previous.fills.length > index ? previous.fills[index] : next.fills[index];
			var state = previous.states.length > index ? previous.states[index] : next.states[index];
			segment.style.setProperty("--pgb-seg-fill", from + "%");
			segment.setAttribute("data-pgb-state", state);
			resetSegmentTiming(segment);
		});

		// Reading layout flushes the suppressed styles, so the release below starts a real
		// transition instead of collapsing into the same style recalculation, and gives the
		// schedule accurate pixel widths to weight each segment's slice by.
		void root.offsetWidth;

		var schedule = scheduleSweep(segments, previous, next);
		var timingByIndex = Object.create(null);
		for (var s = 0; s < schedule.length; s++) timingByIndex[schedule[s].index] = schedule[s];

		// One segment on its own owns the whole journey, so the stylesheet's curve already
		// describes it and nothing needs overriding. Past that the curve has to be split.
		var sliced = schedule.length > 1 && supportsLinearEasing();

		root.removeAttribute("data-pgb-animating");
		eachSegment(segments, function (segment, index) {
			var timing = timingByIndex[index];
			if (timing) {
				var fill = fillOf(segment);
				if (fill) {
					if (sliced) {
						// Every segment runs the full duration off the same clock; its timing
						// function is what holds it still until the leading edge arrives.
						fill.style.transitionDuration = duration + "ms";
						fill.style.transitionDelay = "0ms";
						fill.style.transitionTimingFunction = easeSliceTimingFunction(
							timing.delayFraction,
							timing.delayFraction + timing.share
						);
					} else if (schedule.length > 1) {
						// No multi-stop easing available: fall back to constant speed, handing
						// each segment a slice of the duration proportional to how far it moves.
						fill.style.transitionDuration = Math.max(60, duration * timing.share) + "ms";
						fill.style.transitionDelay = duration * timing.delayFraction + "ms";
						fill.style.transitionTimingFunction = "linear";
					}
				}
			}
			segment.style.setProperty("--pgb-seg-fill", next.fills[index] + "%");
		});

		for (var t = 0; t < schedule.length; t++) {
			(function (it) {
				var segment = segments[it.index];
				var was = previous.states.length > it.index ? previous.states[it.index] : next.states[it.index];
				var becomes = next.states[it.index];
				if (was === becomes) return;

				if (becomes === "unlocked") {
					// Arrives exactly when this segment's own stretch of the sweep finishes, so a
					// milestone crossed midway through a multi-segment update pops the instant
					// the fill reaches it instead of waiting for the whole journey to end. The
					// easing has to be undone to turn distance covered back into a moment in time.
					var travelled = it.delayFraction + it.share;
					var arrivesAt = duration * (sliced ? easeInOutInverse(travelled) : travelled);
					later(function () {
						segment.setAttribute("data-pgb-state", "unlocked");
						// Celebration marks a tier being won. On the intro every already-earned
						// tier crosses, so firing it there would set the whole bar off on open.
						if (config.celebrate && !isIntro) celebrate(segment);
					}, arrivesAt);
				} else {
					// Rewinding leaves 100% immediately, so relock without waiting.
					segment.setAttribute("data-pgb-state", "locked");
				}
			})(schedule[t]);
		}
	}

	function hydrate(root) {
		if (root.getAttribute("data-pgb-ready") === "true") return null;
		root.setAttribute("data-pgb-ready", "true");

		var measured = measure(root);
		if (!measured) return null;

		paint(root);
		return measured;
	}

	function eachSegment(segments, fn) {
		for (var i = 0; i < segments.length; i++) fn(segments[i], i);
	}

	// ----------------------------------------------------------------------------------
	// Free gift reconciliation
	// ----------------------------------------------------------------------------------

	function giftTiersOf(measured) {
		var tiers = [];
		if (!measured) return tiers;
		for (var i = 0; i < measured.tiers.length; i++) {
			var tier = measured.tiers[i];
			if (tier.giftId && tier.giftAvailable && !giftBlocklist[tier.giftId]) tiers.push(tier);
		}
		return tiers;
	}

	function qualifyingValue(cart, mode, giftIds, useOriginal) {
		var giftValue = 0;
		var giftQty = 0;
		for (var i = 0; i < cart.items.length; i++) {
			var item = cart.items[i];
			if (giftIds.indexOf(item.variant_id) === -1) continue;
			giftValue += (useOriginal ? item.original_line_price : item.final_line_price) || 0;
			giftQty += item.quantity || 0;
		}
		if (mode === "quantity") return Math.max(0, (cart.item_count || 0) - giftQty);
		return Math.max(0, (cart.total_price || 0) - giftValue);
	}

	function refreshDrawer() {
		var container = document.querySelector("cart-drawer");
		if (!container || !window.CartApi || !window.cartFunctions) return Promise.resolve();

		var handle = (window.getCartDrawerSectionHandle && window.getCartDrawerSectionHandle()) || "cart-drawer";
		return window.CartApi.fetchSectionHtml(handle).then(function (html) {
			var next = window.CartApi.parseSection(html, "#CartDrawer");
			if (next) window.cartFunctions.updateCartDrawer(container, next);
			if (window.publish && window.PUB_SUB_EVENTS) {
				window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: "cart-rewards-gift" });
			}
		});
	}

	function reconcileGifts() {
		if (giftBusy || !window.CartApi) return;

		var measured = latestMeasure;
		var tiers = giftTiersOf(measured);
		if (!tiers.length) return;

		var ids = tiers.map(function (tier) {
			return tier.giftId;
		});
		// One cart read per genuine change: without this guard every drawer event would fetch.
		var signature = measured.value + "|" + ids.join(",");
		if (signature === giftSignature) return;
		giftSignature = signature;

		giftBusy = true;
		window.CartApi.get()
			.then(function (cart) {
				if (!cart || !cart.items) return null;

				var value = qualifyingValue(cart, measured.mode, ids, measured.useOriginal);
				var inCart = {};
				for (var i = 0; i < cart.items.length; i++) inCart[cart.items[i].variant_id] = true;

				var additions = [];
				var removals = {};
				for (var t = 0; t < tiers.length; t++) {
					var tier = tiers[t];
					var earned = value >= tier.threshold;
					var present = !!inCart[tier.giftId];
					if (earned && !present) additions.push({ id: tier.giftId, quantity: 1 });
					else if (!earned && present) removals[tier.giftId] = 0;
				}

				if (!additions.length && !Object.keys(removals).length) return null;

				// Removals run first so the qualifying total is already settled when gifts are
				// added, which keeps a single pass from granting a tier the cart no longer holds.
				var work = Object.keys(removals).length
					? window.CartApi.update({ updates: removals })
					: Promise.resolve();

				return work
					.then(function () {
						if (!additions.length) return null;
						return window.CartApi.add({ items: additions }).then(function (response) {
							// A gift that cannot be added (sold out between render and click) is
							// parked so the next pass does not retry it forever.
							if (response && !response.ok) {
								for (var a = 0; a < additions.length; a++) giftBlocklist[additions[a].id] = true;
							}
							return null;
						});
					})
					.then(refreshDrawer);
			})
			.catch(function () {
				giftSignature = null;
			})
			.then(function () {
				giftBusy = false;
			});
	}

	// ----------------------------------------------------------------------------------

	function render() {
		renderScheduled = false;

		var roots = document.querySelectorAll(ROOT_SELECTOR);
		for (var i = 0; i < roots.length; i++) {
			var measured = hydrate(roots[i]);
			if (measured) latestMeasure = measured;
		}
		if (roots.length) reconcileGifts();
	}

	// Microtask rather than rAF: a drawer swap has to be reconciled in the same checkpoint as
	// the mutation that caused it, otherwise the bar paints one frame at its final value and
	// the animation is lost.
	function scheduleRender() {
		if (renderScheduled) return;
		renderScheduled = true;
		if (typeof queueMicrotask === "function") queueMicrotask(render);
		else Promise.resolve().then(render);
	}

	// Every node inserted anywhere on the page reaches this observer, so the test has to be
	// cheap and it has to be specific: only a bar actually entering the DOM is worth waking
	// the engine for. Reacting to any mutation meant unrelated churn (lazy images, carousels,
	// app embeds) paid for a full render pass, which adds up to real jank on a busy page.
	function addsRewardsBar(record) {
		var nodes = record.addedNodes;
		for (var i = 0; i < nodes.length; i++) {
			var node = nodes[i];
			if (node.nodeType !== 1) continue;
			if (node.matches(ROOT_SELECTOR) || node.querySelector(ROOT_SELECTOR)) return true;
		}
		return false;
	}

	function observe() {
		if (domObserver) return;
		domObserver = new MutationObserver(function (records) {
			for (var i = 0; i < records.length; i++) {
				if (addsRewardsBar(records[i])) {
					scheduleRender();
					return;
				}
			}
		});
		domObserver.observe(document.body, { childList: true, subtree: true });
	}

	function init() {
		observe();
		render();

		["cart-drawer:update", "cart-drawer:render", "cart-drawer:open", "cart:updated"].forEach(function (name) {
			document.addEventListener(name, scheduleRender);
		});

		document.addEventListener("shopify:section:load", scheduleRender);
		document.addEventListener("shopify:block:select", scheduleRender);

		if (window.subscribe && window.PUB_SUB_EVENTS) {
			window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (event) {
				if (event && event.source === "cart-rewards-gift") return;
				scheduleRender();
			});
		}
	}

	window.Elixir_CartRewards = { render: render, refresh: scheduleRender };

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
